import React, {useContext, useEffect, useMemo, useReducer, useRef, useState} from 'react'
import {Box, Fab, Grid, Switch, Tooltip, Typography} from "@material-ui/core/index";
import {ReactGrid} from 'components/helpers/reactGrid/reactGrid';
import {useSnackbar} from "notistack";
import './articulos.list.scss';
import SubHeader from "../../helpers/subHeader/subHeader";
import {ArticuloDetailsButton} from "components/helpers/articulos/articuloDetails";
import VentasSettings from "components/estadisticas/settings/ventas.settings";
import {useArticulosService, useListadosService} from "services/hooks";
import {generalReducer} from "components/helpers/reducers/reducers";
import {INITIAL_FILTER_SETTINGS_STATE} from "components/helpers/initialFilterSettingsState";
import {BlurOnRounded, SettingsRounded} from "@material-ui/icons";
import {sortNumeric} from "assets/utils";
import {MainContext} from "contexts/main.context";
import {BarcodeWithEdit} from "components/helpers/articulos/articulo.barcode";
import {ExportOptions} from "components/helpers/exportOptions/export.options";
import moment from "moment";

const getTableArticulos = (role, callbackEditFn) => {
    let tableArticulos = {
        columns: [
            {name: 'id', title: 'Código'},
            {name: 'detalle', title: 'Descripción', style: {whiteSpace: 'break-spaces'}},
            {name: 'lista1', title: 'Lista 1'},
            {name: 'total', title: 'Total'},
            {name: 'ubicacion', title: 'Ubic.'},
            {name: 'custom1', title: 'Código Barras'},
            {name: 'custom2', title: ' '},
            {name: 'codigobarr', title: 'Código Barras (solo búsqueda)'}
        ],
        columnExtensions: [
            {columnName: 'id', width: 140},
            {columnName: 'lista1', width: 130, type: 'currency', compare: sortNumeric},
            {columnName: 'total', width: 80, compare: sortNumeric},
            {columnName: 'ubicacion', width: 80},
            {columnName: 'custom1', width: 150, component: (row) => BarcodeWithEdit(row, callbackEditFn)},
            {columnName: 'custom2', width: 100, component: ArticuloDetailsButton, align: 'center'},
            {columnName: 'codigobarr', width: 0}
        ]
    };

    // Quito columnas de precios si el usuario es operador logístico
    if (role === 'L') {
        tableArticulos.columns = tableArticulos.columns.filter(column => !['lista1', 'custom1'].includes(column.name));
        tableArticulos.columnExtensions = tableArticulos.columnExtensions.filter(columnExtension => !['lista1', 'custom1'].includes(columnExtension.columnName));
    }

    return tableArticulos;
};

export function ArticulosList() {
    const [_settingsState, dispatch] = useReducer(generalReducer, INITIAL_FILTER_SETTINGS_STATE);
    const [settingsState, setSettingsState] = useState(INITIAL_FILTER_SETTINGS_STATE);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [articulos, setArticulos] = useState([]);
    // El precio de lista se puede ocultar de la tabla y además se agrega al PDF / Excel exportado.
    const [mostrarPrecioLista, setMostrarPrecioLista] = useState(true);

    // Ref y no variable local: tiene que sobrevivir a los re-renders (por ejemplo al
    // tocar un switch) para que el PDF use las filas que informó la grilla.
    const filteredRows = useRef([]);

    const ArticulosService = useArticulosService();
    const ListadosService = useListadosService();
    const {enqueueSnackbar, closeSnackbar} = useSnackbar();
    const context = useContext(MainContext);

    const userRole = context.loggedUser.role;
    const [tableArticulos, setTableArticulos] = useState(getTableArticulos(userRole, () => loadArticulos(settingsState)));

    useEffect(() => {
        loadArticulos(settingsState);
    }, [settingsState]);

    const loadArticulos = settingsState => {
        ArticulosService.getAllWithFilters(
            settingsState,
            response => setArticulos(response.data.result),
            error => enqueueSnackbar(error, {variant: 'error'}));
    }

    const onConfirmSettingsDialog = () => {
        setSettingsState(_settingsState);
        setSettingsDialogOpen(false);
    }

    const onCancelSettingsDialog = () => {
        dispatch({payload: settingsState, type: 'fullstatechange'});
        setSettingsDialogOpen(false);
    }

    // El PDF y el Excel reciben los mismos parámetros: filtros, filas visibles de la grilla y switch de precio.
    const exportStock = (serviceMethod, fnSuccess) => {
        // Armo los filtros para que busque por fechas de los últimos 3 meses (solo afecta al promedio de ventas menssual).
        const quantityOfMonths = 3;

        const dateFrom = moment().subtract(quantityOfMonths, 'month');
        settingsState.dates = {...settingsState.dates, from: dateFrom};

        serviceMethod.call(
            ListadosService,
            settingsState,
            filteredRows.current.map(row => row.id),
            quantityOfMonths,
            mostrarPrecioLista,
            file => fnSuccess(file),
            error => enqueueSnackbar(error, {variant: 'error'})
        )
    };

    // Se calcula al momento de descargar: stock_articulos_{YYYYMMDDHHMMSS}.{pdf/xlsx}
    const getFileName = extension => `stock_articulos_${moment().format('YYYYMMDDHHmmss')}.${extension}`;

    const getPDF = fnSuccess => exportStock(ListadosService.stockArticulos, fnSuccess);
    const getExcel = fnSuccess => exportStock(ListadosService.stockArticulosExcel, fnSuccess);

    const onFilterRows = _filteredRows => filteredRows.current = _filteredRows;

    const {columnExtensions} = tableArticulos;

    // ReactGrid no muestra las columnas con visible: false.
    const columns = useMemo(
        () => tableArticulos.columns.map(column => column.name === 'lista1' && !mostrarPrecioLista ? {...column, visible: false} : column),
        [tableArticulos, mostrarPrecioLista]
    );

    // El operador logístico no tiene columna de precios (ver getTableArticulos).
    // Mismo formato que el switch "Comparar precios" de Listas de Precio.
    const opcionesColumnas = userRole !== 'L' ? (
        <Grid container className={'m-2'}>
            <Tooltip title={'Muestra el precio de lista en la tabla y lo agrega como columna al exportar el PDF o el Excel'}>
                <Box>
                    <Switch
                        checked={mostrarPrecioLista}
                        onChange={e => setMostrarPrecioLista(e.target.checked)}
                    />
                    <Typography variant={"body2"} component={"span"}>Precio de lista</Typography>
                </Box>
            </Tooltip>
        </Grid>
    ) : null;

    return (
        <>
            <SubHeader title={'Artículos'}/>
            <Box className='container-xl mt-3 mb-3'>
                {opcionesColumnas}
                <Box className='mt-1'>
                    <ReactGrid
                        title={`Artículos (${articulos.length})`}
                        columns={columns}
                        columnExtensions={columnExtensions}
                        rows={articulos}
                        height={-200}
                        enableFiltering={false}
                        onFilterRows={onFilterRows}
                    />
                    <Tooltip title={'Configurar filtros'}>
                        <Box className={'niquel-fab-top-btn'}>
                            <Fab onClick={() => setSettingsDialogOpen(true)} color={"default"}>
                                <SettingsRounded/>
                            </Fab>
                        </Box>
                    </Tooltip>
                </Box>
                {["A", "O", "S"].includes(context.loggedUser.role) && (
                    <ExportOptions
                        enableDownload
                        enableMail
                        enablePrint
                        enableShow
                        enableExcel
                        fabIcon={<BlurOnRounded/>}
                        getPDF={getPDF}
                        getExcel={getExcel}
                        fileName={() => getFileName('pdf')}
                        excelFileName={() => getFileName('xlsx')}
                        mailSubject={"Stock Artículos"}
                        mailBody={"Estimado,  \n\nSe adjunta el listado de artículos en stock."}
                    />
                )}
            </Box>
            <VentasSettings
                open={settingsDialogOpen}
                title={'Filtros y confirguración'}
                onCancel={onCancelSettingsDialog}
                onConfirm={onConfirmSettingsDialog}
                fullScreen={true}
                maxWidth={'lg'}
                state={_settingsState}
                dispatch={dispatch}
                enabledOnlyTabs={['articulos', 'proveedores']}
            />
        </>
    )
}
